// Determine the base URL based on environment
const getBaseUrl = () => {
  // In development, use localhost
  if (process.env.NODE_ENV === 'development') {
    return 'http://localhost:8000';
  }
  // In production, use relative URLs (will go through Nginx proxy)
  return '';
};

import { getSessionToken } from "./utils/auth";

function authHeaders() {
  const token = getSessionToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const profileCache = new Map();
const profileRequestCache = new Map();

const getProfileCacheKey = (profileName, venue = "marrickville") => `${venue}:${profileName}`;

export async function fetchScrapedData() {
  const baseUrl = getBaseUrl();
  const response = await fetch(`${baseUrl}/api/results`);
  if (!response.ok) throw new Error("Failed to fetch scraped data");
  return response.json();
}

// Starting a scrape is admin-only, so the session token must be sent. A 403 here
// means the signed-in user isn't on the server's ADMIN_EMAILS allowlist.
export async function triggerScrape() {
  const baseUrl = getBaseUrl();
  const response = await fetch(`${baseUrl}/api/scrape`, { headers: authHeaders() });
  if (response.status === 401 || response.status === 403) {
    throw new Error("You need to be signed in as an admin to start a scrape.");
  }
  if (!response.ok) throw new Error("Failed to trigger scrape");
  return response.json();
}

export async function fetchScrapeStatus() {
  const baseUrl = getBaseUrl();
  const response = await fetch(`${baseUrl}/api/scrape/status`);
  if (!response.ok) throw new Error("Failed to fetch scrape status");
  return response.json();
}

export async function fetchVisitorCount() {
  const baseUrl = getBaseUrl();
  const response = await fetch(`${baseUrl}/api/visitors`);
  if (!response.ok) throw new Error("Failed to fetch visitor count");
  return response.json();
}

export async function registerVisit() {
  const baseUrl = getBaseUrl();
  const response = await fetch(`${baseUrl}/api/visitors`, { method: "POST" });
  if (!response.ok) throw new Error("Failed to register visit");
  return response.json();
}

export function getCachedProfile(profileName, venue = "marrickville") {
  return profileCache.get(getProfileCacheKey(profileName, venue)) || null;
}

export async function fetchProfile(profileName, venue="marrickville") {
  const baseUrl = getBaseUrl();
  const cacheKey = getProfileCacheKey(profileName, venue);
  const cached = profileCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const inFlight = profileRequestCache.get(cacheKey);
  if (inFlight) {
    return inFlight;
  }

  const request = fetch(`${baseUrl}/api/profiles/${venue}/${encodeURIComponent(profileName)}`)
    .then(async (response) => {
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error("Profile details not found.");
        }
        throw new Error(`Error: ${response.status}`);
      }

      const data = await response.json();
      profileCache.set(cacheKey, data);
      return data;
    })
    .finally(() => {
      profileRequestCache.delete(cacheKey);
    });

  profileRequestCache.set(cacheKey, request);
  return request;
}

export function prefetchProfile(profileName, venue = "marrickville") {
  const cacheKey = getProfileCacheKey(profileName, venue);
  if (profileCache.has(cacheKey)) {
    return Promise.resolve(profileCache.get(cacheKey));
  }

  return fetchProfile(profileName, venue).catch(() => null);
}

// ---------------------------------------------------------------------------
// Notifications: passwordless auth + performer subscriptions
// ---------------------------------------------------------------------------

export async function requestMagicLink(email) {
  const baseUrl = getBaseUrl();
  const response = await fetch(`${baseUrl}/api/auth/request-link`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  if (!response.ok) throw new Error("Failed to request sign-in link");
  return response.json();
}

export async function verifyMagicLink(token) {
  const baseUrl = getBaseUrl();
  const response = await fetch(`${baseUrl}/api/auth/verify?token=${encodeURIComponent(token)}`);
  if (!response.ok) throw new Error("Failed to verify sign-in link");
  return response.json();
}

export async function fetchMe() {
  const baseUrl = getBaseUrl();
  const response = await fetch(`${baseUrl}/api/auth/me`, { headers: authHeaders() });
  if (!response.ok) throw new Error("Failed to fetch account info");
  return response.json();
}

export async function logoutRequest() {
  const baseUrl = getBaseUrl();
  const response = await fetch(`${baseUrl}/api/auth/logout`, {
    method: "POST",
    headers: authHeaders(),
  });
  if (!response.ok) throw new Error("Failed to log out");
  return response.json();
}

export async function fetchSubscriptions() {
  const baseUrl = getBaseUrl();
  const response = await fetch(`${baseUrl}/api/subscriptions`, { headers: authHeaders() });
  if (!response.ok) throw new Error("Failed to fetch subscriptions");
  return response.json();
}

export async function subscribePerformer(venue, performerName) {
  const baseUrl = getBaseUrl();
  const response = await fetch(`${baseUrl}/api/subscriptions`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({ venue, performer_name: performerName }),
  });
  if (!response.ok) throw new Error("Failed to subscribe");
  return response.json();
}

export async function unsubscribePerformer(venue, performerName) {
  const baseUrl = getBaseUrl();
  const response = await fetch(`${baseUrl}/api/subscriptions`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({ venue, performer_name: performerName }),
  });
  if (!response.ok) throw new Error("Failed to unsubscribe");
  return response.json();
}

// ---------------------------------------------------------------------------
// Admin
// ---------------------------------------------------------------------------

export async function fetchSubscribers() {
  const baseUrl = getBaseUrl();
  const response = await fetch(`${baseUrl}/api/admin/subscribers`, {
    headers: authHeaders(),
  });
  if (response.status === 401 || response.status === 403) {
    throw new Error("Admin access required.");
  }
  if (!response.ok) throw new Error("Failed to load subscribers");
  return response.json();
}
